import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ZodIssue, ZodSchema } from 'zod';

type FormErrors<TValues> = Partial<Record<keyof TValues, string>>;

type FormStatus = 'idle' | 'validating' | 'submitting' | 'success' | 'error';

export interface UseFormControllerOptions<TValues> {
  initialValues: TValues;
  schema?: ZodSchema<TValues>;
  validate?: (values: TValues) => FormErrors<TValues>;
  onSubmit: (values: TValues) => void | Promise<void>;
  onSuccess?: (values: TValues) => void;
  onError?: (error: unknown) => void;
  mapSubmitError?: (error: unknown) => { message?: string; fieldErrors?: FormErrors<TValues> };
  isDirtyCompare?: (a: TValues, b: TValues) => boolean;
}

export interface FormFieldState<TValues, K extends keyof TValues> {
  name: string;
  value: TValues[K];
  error?: string;
  touched: boolean;
  onChange: (value: TValues[K] | ChangeEventValue) => void;
  onBlur: () => void;
}

export interface UseFormControllerResult<TValues> {
  values: TValues;
  errors: FormErrors<TValues>;
  touched: Set<keyof TValues>;
  status: FormStatus;
  isSubmitting: boolean;
  isValidating: boolean;
  isDirty: boolean;
  isFieldDirty: (key: keyof TValues) => boolean;
  submitError: string | null;
  submitSuccess: boolean;
  setValue: <K extends keyof TValues>(key: K, value: TValues[K]) => void;
  setValues: (next: TValues) => void;
  setErrors: (next: FormErrors<TValues>) => void;
  reset: (next?: TValues) => void;
  validate: () => Promise<boolean>;
  handleSubmit: (event?: { preventDefault?: () => void }) => Promise<boolean>;
  field: <K extends keyof TValues>(key: K) => FormFieldState<TValues, K>;
  selector: <TSelected>(
    select: (state: Readonly<UseFormControllerResult<TValues>>) => TSelected,
    equals?: (a: TSelected, b: TSelected) => boolean,
  ) => TSelected;
}

type ChangeEventValue = { target?: { value?: unknown; checked?: boolean; type?: string } };

const VALIDATION_DEBOUNCE_MS = 200;

function extractValue<T>(value: T | ChangeEventValue): T {
  if (
    value !== null &&
    typeof value === 'object' &&
    'target' in (value as Record<string, unknown>)
  ) {
    const target = (value as ChangeEventValue).target;
    if (!target) {
      return value as T;
    }
    if (target.type === 'checkbox') {
      return target.checked as T;
    }
    return (target.value as T) ?? (value as T);
  }
  return value as T;
}

function mapZodIssues<TValues>(issues: readonly ZodIssue[]): FormErrors<TValues> {
  const errors: FormErrors<TValues> = {};
  for (const issue of issues) {
    const pathKey = issue.path.at(0);
    if (typeof pathKey === 'string') {
      errors[pathKey as keyof TValues] = issue.message;
    }
  }
  return errors;
}

function defaultDeepEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

export function useFormController<TValues extends Record<string, unknown>>(
  options: UseFormControllerOptions<TValues>,
): UseFormControllerResult<TValues> {
  const {
    initialValues,
    schema,
    validate: validateFn,
    onSubmit,
    onSuccess,
    onError,
    mapSubmitError,
    isDirtyCompare,
  } = options;

  const [values, setValuesState] = useState<TValues>(initialValues);
  const [errors, setErrorsState] = useState<FormErrors<TValues>>({});
  const [touched, setTouched] = useState<Set<keyof TValues>>(new Set());
  const [status, setStatus] = useState<FormStatus>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const initialRef = useRef<TValues>(initialValues);
  const lastSuccessfulRef = useRef<TValues | null>(null);

  const setValue = useCallback(<K extends keyof TValues>(key: K, value: TValues[K]) => {
    setValuesState((current) => ({ ...current, [key]: value }));
  }, []);

  const setValues = useCallback((next: TValues) => {
    setValuesState(next);
  }, []);

  const setErrors = useCallback((next: FormErrors<TValues>) => {
    setErrorsState(next);
  }, []);

  const markTouched = useCallback((key: keyof TValues) => {
    setTouched((current) => {
      if (current.has(key)) {
        return current;
      }
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  const computeErrors = useCallback(
    (nextValues: TValues): FormErrors<TValues> => {
      let nextErrors: FormErrors<TValues> = {};

      if (schema) {
        const result = schema.safeParse(nextValues);
        if (!result.success) {
          nextErrors = { ...nextErrors, ...mapZodIssues<TValues>(result.error.issues) };
        }
      }

      if (validateFn) {
        const customErrors = validateFn(nextValues);
        nextErrors = { ...nextErrors, ...customErrors };
      }

      return nextErrors;
    },
    [schema, validateFn],
  );

  const validate = useCallback((): Promise<boolean> => {
    setStatus('validating');
    setSubmitSuccess(false);

    const nextErrors = computeErrors(values);

    setErrorsState(nextErrors);

    const hasErrors = Object.keys(nextErrors).length > 0;
    setStatus(hasErrors ? 'error' : 'idle');
    return Promise.resolve(!hasErrors);
  }, [computeErrors, values]);

  useEffect(() => {
    if (touched.size === 0) {
      return;
    }
    if (!schema && !validateFn) {
      return;
    }

    const handle = window.setTimeout(() => {
      const nextErrors = computeErrors(values);
      setErrorsState((current) => {
        const next = { ...current };
        touched.forEach((key) => {
          const message = nextErrors[key];
          if (typeof message === 'string' && message.length > 0) {
            next[key] = message;
          } else {
            delete next[key];
          }
        });
        return next;
      });
    }, VALIDATION_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [computeErrors, schema, touched, validateFn, values]);

  const markAllTouched = useCallback(() => {
    const allKeys = Object.keys(values) as Array<keyof TValues>;
    setTouched(new Set(allKeys));
  }, [values]);

  const handleSubmit = useCallback(
    async (event?: {
      preventDefault?: () => void;
      currentTarget?: EventTarget | null;
    }): Promise<boolean> => {
      const formTarget = event?.currentTarget;
      const hasHtmlFormTarget =
        typeof HTMLFormElement !== 'undefined' && formTarget instanceof HTMLFormElement;

      if (hasHtmlFormTarget && !formTarget.reportValidity()) {
        setErrorsState({});
        setSubmitError(null);
        setSubmitSuccess(false);
        return false;
      }

      event?.preventDefault?.();
      setSubmitError(null);
      setSubmitSuccess(false);
      markAllTouched();
      const isValid = await validate();
      if (!isValid) {
        return false;
      }
      setStatus('submitting');
      try {
        await onSubmit(values);
        setStatus('success');
        setSubmitSuccess(true);
        onSuccess?.(values);
        lastSuccessfulRef.current = values;
        return true;
      } catch (unknownError) {
        setStatus('error');
        const mapped = mapSubmitError ? mapSubmitError(unknownError) : null;
        if (mapped?.fieldErrors) {
          setErrorsState(mapped.fieldErrors);
        }
        const message =
          mapped?.message ??
          (unknownError instanceof Error ? unknownError.message : 'Unable to submit form.');
        setSubmitError(message);
        if (onError) {
          onError(unknownError);
        }
        return false;
      }
    },
    [markAllTouched, mapSubmitError, onError, onSubmit, onSuccess, validate, values],
  );

  const reset = useCallback((next?: TValues) => {
    const nextValues = next ?? initialRef.current;
    initialRef.current = nextValues;
    setValuesState(nextValues);
    setErrorsState({});
    setTouched(new Set());
    setSubmitError(null);
    setSubmitSuccess(false);
    setStatus('idle');
  }, []);

  const field = useCallback(
    <K extends keyof TValues>(key: K): FormFieldState<TValues, K> => ({
      name: String(key),
      value: values[key],
      error: errors[key],
      touched: touched.has(key),
      onChange: (next) => {
        const resolved = extractValue<TValues[K]>(next);
        setValue(key, resolved);
        markTouched(key);
      },
      onBlur: () => {
        markTouched(key);
      },
    }),
    [errors, markTouched, setValue, touched, values],
  );

  const deepEqual = useMemo(() => {
    if (isDirtyCompare) {
      return isDirtyCompare;
    }
    return defaultDeepEqual;
  }, [isDirtyCompare]);

  const isDirty = useMemo(() => {
    return !deepEqual(values, initialRef.current);
  }, [deepEqual, values]);

  const isFieldDirty = useCallback(
    (key: keyof TValues) => {
      return !defaultDeepEqual(values[key], initialRef.current[key]);
    },
    [values],
  );

  const selectorCacheRef = useRef<unknown>(undefined);

  const selector = useCallback(
    <TSelected>(
      select: (state: Readonly<UseFormControllerResult<TValues>>) => TSelected,
      equals: (a: TSelected, b: TSelected) => boolean = Object.is,
    ): TSelected => {
      const snapshot = {
        values,
        errors,
        touched,
        status,
        isSubmitting: status === 'submitting',
        isValidating: status === 'validating',
        isDirty,
        isFieldDirty,
        submitError,
        submitSuccess,
        setValue,
        setValues,
        setErrors,
        reset,
        validate,
        handleSubmit,
        field,
        selector: () => {
          throw new Error('Nested selector calls are not supported.');
        },
      } satisfies UseFormControllerResult<TValues>;

      const next = select(snapshot);
      const previous = selectorCacheRef.current as TSelected | undefined;
      if (previous !== undefined && equals(previous, next)) {
        return previous;
      }
      selectorCacheRef.current = next;
      return next;
    },
    [
      errors,
      field,
      handleSubmit,
      isDirty,
      isFieldDirty,
      reset,
      setErrors,
      setValue,
      setValues,
      status,
      submitError,
      submitSuccess,
      touched,
      validate,
      values,
    ],
  );

  return useMemo(
    (): UseFormControllerResult<TValues> => ({
      values,
      errors,
      touched,
      status,
      isSubmitting: status === 'submitting',
      isValidating: status === 'validating',
      isDirty,
      isFieldDirty,
      submitSuccess,
      submitError,
      setValue,
      setValues,
      setErrors,
      reset,
      validate,
      handleSubmit,
      field,
      selector,
    }),
    [
      errors,
      field,
      handleSubmit,
      isDirty,
      isFieldDirty,
      reset,
      selector,
      setErrors,
      setValue,
      setValues,
      status,
      submitError,
      submitSuccess,
      touched,
      validate,
      values,
    ],
  );
}
